"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.horizonTargetMonths = exports.horizonFillMonths = exports.EXTRAPOLATION_HORIZON_MONTHS = exports.extrapolateMonthlyMap = void 0;
/**
 * CALL-752: forward-extrapolate a monthly budget rate map (minute price / contract ABT) using a
 * 3-month rolling average — the same rule the budget module already uses for volume data. Shared
 * verbatim by the backend (persisted results + handling-cost summary) and the frontend (live cost
 * preview + entry grids), so the client preview can never silently disagree with the server.
 *
 * Behaviour:
 * - Forward-only: fills months strictly AFTER the last entered month, bounded by `targetMonths`.
 *   This preserves CALL-749's guarantee that auto-populated history (back to 2024) never widens
 *   the budget period — only later months are filled, never earlier ones.
 * - Never overwrites an entered value. The input map only holds entered months, so an explicit 0
 *   is present and kept; an empty month is absent (`has` is the empty-vs-zero test, never `=== 0`).
 * - Each extrapolated value feeds back into the rolling window, exactly like the Strat FC rule.
 * - Does NOT fill interior gaps (a month before the last entered one): only future months.
 * - No rounding: minute prices are DECIMAL(10,4) and ABT seconds tolerate fractions; the final cost
 *   is rounded downstream by calculateMonthlyBudgetCost.
 *
 * Month keys are 'YYYY-MM-DD' (first-of-month), so lexicographic order equals chronological order.
 *
 * @param enteredMap   month -> entered value, entered months only.
 * @param targetMonths candidate months to fill (typically the config's forecast-volume months).
 * @returns the filled map plus the list of months that were extrapolated.
 */
const extrapolateMonthlyMap = (enteredMap, targetMonths) => {
    const filled = new Map(enteredMap);
    const extrapolatedMonths = [];
    const dates = [...enteredMap.keys()].sort();
    if (dates.length === 0) {
        // Nothing entered — never fabricate a period out of thin air.
        return { filled, extrapolatedMonths };
    }
    const lastEntered = dates[dates.length - 1];
    const values = dates.map((date) => enteredMap.get(date));
    const rolling = values.slice(-Math.min(3, values.length));
    let avg = rolling.reduce((a, b) => a + b, 0) / rolling.length;
    for (const month of [...targetMonths].sort()) {
        if (month <= lastEntered || filled.has(month)) {
            continue;
        }
        filled.set(month, avg);
        extrapolatedMonths.push(month);
        rolling.push(avg);
        if (rolling.length > 3) {
            rolling.shift();
        }
        avg = rolling.reduce((a, b) => a + b, 0) / rolling.length;
    }
    return { filled, extrapolatedMonths };
};
exports.extrapolateMonthlyMap = extrapolateMonthlyMap;
/**
 * CALL-752: how many months to forward-extrapolate past the last entered rate. Beyond this the
 * projection isn't meaningful, and — crucially — one stray future entry must not fill a whole year.
 * The budget version is no longer a single calendar year (CALL-745), so we bound by this rolling
 * horizon instead of the calendar year.
 */
exports.EXTRAPOLATION_HORIZON_MONTHS = 6;
// Add `count` months to a 'YYYY-MM-01' key. Lexicographic order == chronological for this format.
const addMonths = (month, count) => {
    const year = Number(month.slice(0, 4));
    const zeroBased = Number(month.slice(5, 7)) - 1 + count;
    const newYear = year + Math.floor(zeroBased / 12);
    const newMonth = (((zeroBased % 12) + 12) % 12) + 1;
    return `${newYear}-${String(newMonth).padStart(2, '0')}-01`;
};
/**
 * CALL-752: the contiguous list of the `horizon` months STRICTLY AFTER the last entered month in
 * `enteredMap` — i.e. the forward-fill window itself, GENERATED rather than filtered. Use this (with
 * extrapolateMonthlyMap) when the candidate months don't already exist as keys, e.g. extending
 * forecast volumes past the last populated month. Returns [] when the map is empty.
 */
const horizonFillMonths = (enteredMap, horizon = exports.EXTRAPOLATION_HORIZON_MONTHS) => {
    const entered = [...enteredMap.keys()];
    if (entered.length === 0) {
        return [];
    }
    const lastEntered = entered.reduce((a, b) => (a > b ? a : b));
    const out = [];
    for (let i = 1; i <= horizon; i++) {
        out.push(addMonths(lastEntered, i));
    }
    return out;
};
exports.horizonFillMonths = horizonFillMonths;
/**
 * Candidate months for forward extrapolation, bounded to a fixed HORIZON (default 6 months) past the
 * last entered month in `anchorMap`. Replaces the old calendar-year cap: the budget version is no
 * longer a single year (CALL-745), so a rolling horizon is the right bound — it keeps the budget
 * calculating past the last entered rate (the CALL-752 comfort feature) while stopping a lone future
 * entry from widening the period across a whole year. extrapolateMonthlyMap's forward-only fill then
 * fills only the months strictly after the last entered one, up to this limit.
 *
 * Returns [] when `anchorMap` is empty, so a config with no entered rates is never extrapolated.
 * `anchorMap` is the minute-price map: the cost path gates BOTH price and ABT extrapolation on the
 * priced months, so ABT is bounded by the price horizon too.
 */
const horizonTargetMonths = (anchorMap, candidateMonths, horizon = exports.EXTRAPOLATION_HORIZON_MONTHS) => {
    const entered = [...anchorMap.keys()];
    if (entered.length === 0) {
        return [];
    }
    const lastEntered = entered.reduce((a, b) => (a > b ? a : b));
    const limit = addMonths(lastEntered, horizon);
    return candidateMonths.filter((month) => month <= limit);
};
exports.horizonTargetMonths = horizonTargetMonths;
